import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  Observable,
  ServerError,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { useEffect, useMemo, useRef } from "react";
import config from "../config";

import useCurrentUser from "../hooks/useCurrentUser";
import { CurrentUser } from "../hooks/useCurrentUser/types";
import { sendRaygunError } from "../hooks/useRaygun";
import { isTokenExpired, refreshAsyncToken } from "../utils/auth";
import { errorsVar } from "./makeVars";
import { isPublicPath } from "../screens/NavigatorScreen/utils";

export type TokenRef = Pick<CurrentUser, "token" | "refreshToken" | "expiresAt">;

export const V3_CLIENT_NAME = "v3-api";

export function buildLink({
  setCurrentUser,
  logout,
  tokenRef,
  httpUri,
}: {
  setCurrentUser(user: CurrentUser): void;
  logout(): void;
  tokenRef: { current?: TokenRef };
  httpUri: string;
}) {
  const authLink = setContext((_, { headers }) =>
    (async () => {
      if (!tokenRef.current) {
        logout();
        return;
      }

      if (isTokenExpired(tokenRef.current.expiresAt) && tokenRef.current.refreshToken) {
        try {
          const payload = await refreshAsyncToken(tokenRef.current.refreshToken);
          setCurrentUser(payload);
          tokenRef.current = payload;
        } catch (e) {
          // does nothing when there's an error refreshing token. The graphql call will mostly return 401 and the user will be logged out
          console.log(e);
        }
      }

      return {
        headers: {
          ...headers,
          authorization: tokenRef.current.token ? `Bearer ${tokenRef.current.token}` : "",
          "X-Client-Version": config.appVersion,
          "X-Client-Name": config.appSlug,
        },
      };
    })()
  );

  const errorLink = onError(function ({ networkError, graphQLErrors, forward, operation }) {
    const { callerWillHandleError } = operation.getContext();

    if (networkError && (networkError as ServerError).statusCode === 401) {
      logout();
      return;
    }

    if (callerWillHandleError?.(graphQLErrors, networkError)) {
      return forward(operation);
    }

    if (graphQLErrors?.length) {
      if (callerWillHandleError?.(graphQLErrors)) {
        return forward(operation);
      }
      const errorMessages = graphQLErrors?.map(({ message }) => message).join(", ");
      sendRaygunError(new Error(`GraphqlError: ${errorMessages || "Unknown error"}, operation: ${operation.operationName}`));
    }

    return new Observable((observer) => {
      errorsVar({
        networkError,
        graphQLErrors,
        retry() {
          operation.setContext({ retrying: true });
          forward(operation).subscribe(observer);
        },
      });
    });
  }).split(
    (op) => op.getContext().retrying,
    new ApolloLink((operation, forward) => {
      operation.setContext({ retrying: false });
      return errorLink.request(operation, forward);
    })
  );

  const resetErrorsVarLink = new ApolloLink((operation, forward) =>
    forward(operation).map((data) => {
      errorsVar(undefined);
      return data;
    })
  );

  const httpLink = createHttpLink({
    uri: httpUri,
  });

  return errorLink.concat(resetErrorsVarLink).concat(authLink).concat(httpLink);
}

const GraphqlProvider = ({ children }: { children: React.ReactNode }) => {
  const { token, memberNumber, expiresAt, refreshToken, setCurrentUser, logout } = useCurrentUser();
  const tokenRef = useRef<TokenRef>({ token, expiresAt, refreshToken });

  useEffect(() => {
    tokenRef.current = { token, expiresAt, refreshToken };
  }, [token, expiresAt, refreshToken]);

  const client = useMemo(() => {
    const link = ApolloLink.split(
      (operation) => operation.getContext().clientName === V3_CLIENT_NAME,
      buildLink({ setCurrentUser, logout, tokenRef, httpUri: config.v3ApiUrl }),
      buildLink({ setCurrentUser, logout, tokenRef, httpUri: config.apiUrl })
    );

    const apiOptions = isPublicPath() ? { uri: config.publicApiUrl } : { link };

    return new ApolloClient({
      ...apiOptions,
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              deferredCompPlanTransition: {
                merge: true,
              },
            },
          },
        },
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
        },
      },
    });
  }, [tokenRef]);

  useEffect(() => {
    if (!token && !isPublicPath()) {
      client.clearStore();
    }
  }, [token]);

  return (
    // use the memberNumber as a key to make sure all memoized data from React is cleared when a different user is logged in
    <ApolloProvider key={memberNumber} client={client}>
      {children}
    </ApolloProvider>
  );
};

export default GraphqlProvider;
